import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import DataGrid, { textEditor } from 'react-data-grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const API_URL = process.env.REACT_APP_API_URL;

const EMP_API = `${API_URL}/api/employees`;
const ATT_API = `${API_URL}/api/attendance-records/`;

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();
const getTypeOfDay = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();
  // 0: Sunday, 6: Saturday
  return dayOfWeek === 0 || dayOfWeek === 6 ? 'H' : 'W';
};

export default function AttendanceView({ user }) {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [markAllPresent, setMarkAllPresent] = useState(false);
  const [salaryModalOpen, setSalaryModalOpen] = useState(false);
  const [salaryDetails, setSalaryDetails] = useState(null);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetch(EMP_API).then(r => r.json()).then(setEmployees);
  }, []);

  useEffect(() => {
    if (selected && selected !== 'all') {
      fetch(ATT_API + selected).then(r => r.json()).then(data => {
        setAttendance(data);
      });
    } else if (selected === 'all') {
      // Fetch attendance for all employees
      Promise.all(
        employees.map(emp =>
          fetch(ATT_API + emp.employee_id).then(r => r.json())
        )
      ).then(allData => {
        setAttendance(
          employees.map((emp, idx) => ({ emp, data: allData[idx] }))
        );
      });
    } else {
      setAttendance([]);
    }
  }, [selected, employees]);

  useEffect(() => {
    const days = daysInMonth(year, month);
    const dayCols = Array.from({ length: days }, (_, i) => ({
      key: `d${i + 1}`,
      name: `${i + 1} (${getTypeOfDay(year, month, i + 1)})`,
      editable: true,
      editor: textEditor,
      width: 60
    }));
    setColumns([
      {
        key: 'empId',
        name: 'Employee ID',
        frozen: true,
        formatter: ({ row }) => (
          <span style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleEmpIdClick(row.empId)}>{row.empId}</span>
        )
      },
      { key: 'empName', name: 'Employee Name', frozen: true },
      ...dayCols,
      { key: 'totalDays', name: 'Total Days', width: 120 },
      { key: 'presence', name: 'Presence', width: 80 },
      { key: 'leave', name: 'Leave', width: 60 },
      { key: 'allowed', name: 'Allowed', width: 70, editable: true, editor: textEditor },
      { key: 'leaveBalance', name: 'Leave Balance', width: 120, editable: false },
      { key: 'payable', name: 'Payable Days', width: 110 }
    ]);
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[month - 1];
    async function buildRowsWithLeaveBalance() {
      if (selected === 'all') {
        const newRows = await Promise.all(employees.map(async (emp, idx) => {
          let row = {
            empId: emp.employee_id,
            empName: emp.name,
          };
          for (let i = 1; i <= days; i++) {
            const att = (attendance[idx]?.data || []).find(a => a.date === `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`);
            row[`d${i}`] = att ? att.status : '';
          }
          let presence = 0;
          for (let i = 1; i <= days; i++) {
            const status = row[`d${i}`];
            if (status === 'P' || status === '1') presence += 1;
            else if (status === 'L' || status === '0' || status === '' || status === undefined) {/* do nothing */}
            else if (!isNaN(parseFloat(status))) presence += parseFloat(status);
          }
          let leave = days - presence;
          row.presence = presence;
          row.leave = leave;
          row.allowed = row.allowed || 0;
          // Always use leave_balance from employees table
          row.leaveBalance = emp.leave_balance;
          row.payable = presence + parseFloat(row.allowed || 0);
          row.totalDays = days;
          return row;
        }));
        setRows(newRows);
      } else if (selected) {
        const emp = employees.find(e => e.employee_id === selected);
        let row = {
          empId: emp ? emp.employee_id : '',
          empName: emp ? emp.name : '',
        };
        for (let i = 1; i <= days; i++) {
          const att = attendance.find(a => a.date === `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`);
          row[`d${i}`] = att ? att.status : '';
        }
        let presence = 0;
        for (let i = 1; i <= days; i++) {
          const status = row[`d${i}`];
          if (status === 'P' || status === '1') presence += 1;
          else if (status === 'L' || status === '0' || status === '' || status === undefined) {/* do nothing */}
          else if (!isNaN(parseFloat(status))) presence += parseFloat(status);
        }
        let leave = days - presence;
        row.presence = presence;
        row.leave = leave;
        row.allowed = row.allowed || 0;
        // Always use leave_balance from employees table
        row.leaveBalance = emp ? emp.leave_balance : 0;
        row.payable = presence + parseFloat(row.allowed || 0);
        row.totalDays = days;
        setRows([row]);
      } else {
        setRows([]);
      }
    }
    buildRowsWithLeaveBalance();
  }, [selected, attendance, year, month, employees]);

  useEffect(() => {
    if (markAllPresent && rows.length > 0) {
      const days = daysInMonth(year, month);
      const updatedRows = rows.map(row => {
        let newRow = { ...row };
        for (let i = 1; i <= days; i++) {
          newRow[`d${i}`] = '1';
        }
        // recalc summary
        let presence = 0;
        for (let i = 1; i <= days; i++) {
          const status = newRow[`d${i}`];
          if (status === 'P' || status === '1') presence += 1;
          else if (status === 'L' || status === '0' || status === '' || status === undefined) {/* do nothing */}
          else if (!isNaN(parseFloat(status))) presence += parseFloat(status);
        }
        let leave = days - presence;
        newRow.presence = presence;
        newRow.leave = leave;
        newRow.allowed = newRow.allowed || 0;
        // Always use leave_balance from employees table
        const empObj = employees.find(e => e.employee_id === newRow.empId);
        newRow.leaveBalance = empObj ? empObj.leave_balance : 0;
        newRow.payable = presence + parseFloat(newRow.allowed || 0);
        newRow.totalDays = days;
        return newRow;
      });
      setRows(updatedRows);
    }
    // eslint-disable-next-line
  }, [markAllPresent]);

  useEffect(() => {
    if (!selected && !year && !month) return;
    setMarkAllPresent(false);
  }, [selected, year, month]);

  const onRowsChange = (newRows) => {
    // Recalculate summary columns for all rows, and enforce leave logic
    const days = daysInMonth(year, month);
    const updatedRows = newRows.map(row => {
      let presence = 0;
      for (let i = 1; i <= days; i++) {
        const status = row[`d${i}`];
        if (status === 'P' || status === '1') presence += 1;
        else if (status === 'L' || status === '0' || status === '' || status === undefined) {/* do nothing */}
        else if (!isNaN(parseFloat(status))) presence += parseFloat(status);
      }
      let leave = days - presence;
      let allowed = parseFloat(row.allowed || 0);
      // Get original leave balance from employees table
      const empObj = employees.find(e => e.employee_id === row.empId);
      let originalLeaveBalance = empObj ? parseFloat(empObj.leave_balance) || 0 : 0;
      // Calculate new leave balance for UI
      let leaveBalance = originalLeaveBalance - allowed;
      if (leaveBalance < 0) leaveBalance = 0;
      return {
        ...row,
        presence,
        leave,
        allowed,
        leaveBalance,
        payable: presence + allowed,
        totalDays: days
      };
    });
    setRows(updatedRows);
  };

  const handleSave = async () => {
    const days = daysInMonth(year, month);
    let updates = [];
    // Month names array for conversion
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[month - 1];
    if (selected === 'all') {
      for (const row of rows) {
        for (let i = 1; i <= days; i++) {
          updates.push({
            employee_id: row.empId,
            date: `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`,
            status: row[`d${i}`] || '0',
            year: year,
            month: monthName
          });
        }
      }
    } else if (selected) {
      for (let i = 1; i <= days; i++) {
        updates.push({
          employee_id: rows[0].empId,
          date: `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`,
          status: rows[0][`d${i}`] || '0',
          year: year,
          month: monthName
        });
      }
    }
    for (const u of updates) {
      await fetch(`${API_URL}/api/attendance-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(u)
      });
    }
    // Update leave_balance in employee table after attendance is saved
    if (selected === 'all') {
      for (const row of rows) {
        const emp = employees.find(e => e.employee_id === row.empId);
        if (emp) {
          // Use the updated leaveBalance from the row
          await fetch(`${API_URL}/api/employees/${emp.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...emp, leave_balance: row.leaveBalance })
          });
        }
      }
    } else if (selected) {
      const row = rows[0];
      const emp = employees.find(e => e.employee_id === row.empId);
      if (emp) {
        await fetch(`${API_URL}/api/employees/${emp.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...emp, leave_balance: row.leaveBalance })
        });
      }
    }
    // Save summary for each employee
    if (selected === 'all') {
      for (const row of rows) {
        await fetch(`${API_URL}/api/attendance-summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employee_id: row.empId,
            year: year,
            month: monthName,
            working_days: row.presence,
            payable_days: row.payable,
            leave_allowed: row.allowed,
            leave_balance: row.leaveBalance,
            total_days: row.totalDays,
            leave_taken: row.leave
          })
        });
      }
    } else if (selected) {
      const row = rows[0];
      await fetch(`${API_URL}/api/attendance-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: row.empId,
          year: year,
          month: monthName,
          working_days: row.empId ? row.presence : 0,
          payable_days: row.payable,
          leave_allowed: row.allowed,
          leave_balance: row.leaveBalance,
          total_days: row.totalDays,
          leave_taken: row.leave
        })
      });
    }
    alert('Attendance updated!');
  };

  const handleEmpIdClick = async (empId) => {
    // Fetch attendance summary for the selected employee, year, and month
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = monthNames[month - 1];
    const res = await fetch(`${API_URL}/api/attendance?employee_id=${empId}&year=${year}&month=${monthName}`);
    const att = await res.json();
    setSalaryDetails(att);
    setSalaryModalOpen(true);
  };

  useEffect(() => {
    if (user && user.role === 'employee') {
      setSelected(user.employee_id);
    }
  }, [user]);

  useEffect(() => {
    const now = new Date();
    const selectedDate = new Date(year, month - 1, 1);
    if (selectedDate > now) {
      setError('Please select a valid month and year.');
    } else {
      // Check if selectedDate is more than 6 months before now
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1); // 6 months including current
      if (selectedDate < sixMonthsAgo) {
        setError('Attendance for more than 6 months ago cannot be updated.');
      } else {
        setError('');
      }
    }
  }, [month, year]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Attendance Sheet</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl>
          <InputLabel>Month</InputLabel>
          <Select value={month} label="Month" onChange={e => setMonth(Number(e.target.value))}>
            {[
              'January', 'February', 'March', 'April', 'May', 'June',
              'July', 'August', 'September', 'October', 'November', 'December'
            ].map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            value={selected}
            onChange={e => setSelected(e.target.value)}
            label="Employee"
            disabled={user && user.role === 'employee'}
          >
            {user && user.role === 'employee' ? (
              <MenuItem value={user.employee_id}>{user.name}</MenuItem>
            ) : (
              <MenuItem value="all">All Employees</MenuItem>
            )}
            {user && user.role !== 'employee' && employees.map(emp => (
              <MenuItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FormControlLabel
        control={<Checkbox checked={markAllPresent} onChange={e => setMarkAllPresent(e.target.checked)} />}
        label="Mark All Present"
        sx={{ mb: 2 }}
      />
      <Box sx={{
        width: '100%',
        overflowX: isMobile ? 'auto' : 'visible',
        mt: 2,
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        background: '#fff',
        boxShadow: 1,
        pb: 2,
      }}>
        <div style={{ minWidth: isMobile ? 600 : '100%', fontSize: isMobile ? 12 : 14 }}>
          <DataGrid
            columns={columns.map(col => ({
              ...col,
              headerRenderer: p => <span style={{ fontSize: isMobile ? 12 : 14 }}>{col.name}</span>,
              width: isMobile && col.width ? Math.max(50, col.width - 10) : col.width,
            }))}
            rows={rows}
            onRowsChange={onRowsChange}
            style={{
              minHeight: 350,
              fontSize: isMobile ? 12 : 14,
              '--rdg-cell-padding': isMobile ? '4px' : '8px',
            }}
            enableCellSelect={true}
          />
        </div>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Button variant="contained" onClick={handleSave} disabled={!!error}>Save Attendance</Button>
      <Dialog open={salaryModalOpen} onClose={() => setSalaryModalOpen(false)}>
        <DialogTitle>Salary Details</DialogTitle>
        <DialogContent>
          {salaryDetails ? (
            <Box>
              <Typography><b>Employee UID:</b> {salaryDetails.employee_id}</Typography>
              <Typography><b>Employee Name:</b> {salaryDetails.name}</Typography>
              <Typography><b>Designation:</b> {salaryDetails.position}</Typography>
              <Typography><b>Month:</b> {salaryDetails.month}</Typography>
              <Typography><b>Year:</b> {salaryDetails.year}</Typography>
              <Typography><b>Working Days:</b> {salaryDetails.working_days}</Typography>
              <Typography><b>Allowed Leaves:</b> {salaryDetails.leave_allowed}</Typography>
              <Typography><b>Leave Taken:</b> {salaryDetails.leave_taken}</Typography>
              <Typography><b>Days Payable:</b> {salaryDetails.payable_days}</Typography>
              <Typography><b>CTC:</b> {salaryDetails.ctc}</Typography>
              <Typography><b>Actual Salary:</b> {salaryDetails.actual_salary}</Typography>
              <Typography><b>Basic:</b> {salaryDetails.basic}</Typography>
              <Typography><b>House Rent Allowance:</b> {salaryDetails.hra}</Typography>
              <Typography><b>Food Allowance:</b> {salaryDetails.food_allowance}</Typography>
              <Typography><b>Employee Engagement Fund:</b> {salaryDetails.engagement_fund}</Typography>
              <Typography><b>Telephone And Internet Allow:</b> {salaryDetails.telephone_internet}</Typography>
              <Typography><b>Total Allowances:</b> {salaryDetails.total_allowances}</Typography>
              <Typography><b>Gross Salary:</b> {salaryDetails.gross_salary}</Typography>
              <Typography><b>TDS Rate:</b> {salaryDetails.tds_rate}</Typography>
              <Typography><b>TDS:</b> {salaryDetails.tds}</Typography>
              <Typography><b>Net Salary:</b> {salaryDetails.net_salary}</Typography>
              <Typography><b>Bank Name:</b> {salaryDetails.bank_name}</Typography>
              <Typography><b>Branch:</b> {salaryDetails.branch}</Typography>
              <Typography><b>Bank Account Number:</b> {salaryDetails.bank_account_number}</Typography>
              <Typography><b>Arrier:</b> {salaryDetails.arrears}</Typography>
              <Typography><b>Leave Balance:</b> {salaryDetails.leave_balance}</Typography>
            </Box>
          ) : (
            <Typography>No details found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 